import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import PageTitle from "@/components/PageTitle";
import FullScreenLoader from "@/components/loaders/screenLoader";
import MetricCard from "@/components/analytics/MetricCard";
import Eyebrow from "@/components/Eyebrow";
import EmptyState from "@/components/EmptyState";
import {
  exportIntelligenceInvoices,
  fetchIntelligenceDashboard,
  fetchIntelligenceFeatureFlags,
  fetchIntelligenceFreshness,
  fetchIntelligenceLineage,
  fetchIntelligenceObservability,
  fetchIntelligenceRevenue,
  fetchIntelligenceTiers,
  fetchIntelligenceUsage,
} from "@/services/api/intelligence.api";
import type {
  IntelligenceDashboardResponse,
  IntelligenceFeatureFlag,
  IntelligenceFreshnessRow,
  IntelligenceLineageRow,
  IntelligenceObservabilitySummary,
  IntelligenceRevenueSummary,
  IntelligenceTierConfig,
  IntelligenceUsageResponse,
} from "@/types/intelligence";

const numberFormatter = new Intl.NumberFormat();
const twoDecimalFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatNumber = (value: number) => numberFormatter.format(Math.round(value));
const formatLatency = (value: number) => `${twoDecimalFormatter.format(value)} ms`;
const formatCurrency = (value: number) => `$${twoDecimalFormatter.format(value)}`;
const formatFreshness = (seconds: number) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${twoDecimalFormatter.format(seconds / 3600)}h`;
};

const monthDefault = new Date().toISOString().slice(0, 7);

const IntelligencePage = () => {
  const [dashboard, setDashboard] = useState<IntelligenceDashboardResponse | null>(null);
  const [usage, setUsage] = useState<IntelligenceUsageResponse | null>(null);
  const [observability, setObservability] =
    useState<IntelligenceObservabilitySummary | null>(null);
  const [revenue, setRevenue] = useState<IntelligenceRevenueSummary | null>(null);
  const [tiers, setTiers] = useState<IntelligenceTierConfig[]>([]);
  const [featureFlags, setFeatureFlags] = useState<IntelligenceFeatureFlag[]>([]);
  const [freshnessRows, setFreshnessRows] = useState<IntelligenceFreshnessRow[]>([]);
  const [lineageRows, setLineageRows] = useState<IntelligenceLineageRow[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const [invoiceMonth, setInvoiceMonth] = useState(monthDefault);
  const [exporting, setExporting] = useState(false);
  const [usageDays, setUsageDays] = useState(30);

  const load = async (days: number, month: string) => {
    setStatus("loading");
    setError(null);
    try {
      const [
        dashboardResponse,
        usageResponse,
        observabilityResponse,
        revenueResponse,
        tiersResponse,
        flagsResponse,
        freshnessResponse,
        lineageResponse,
      ] = await Promise.all([
        fetchIntelligenceDashboard(),
        fetchIntelligenceUsage(days),
        fetchIntelligenceObservability(),
        fetchIntelligenceRevenue(month),
        fetchIntelligenceTiers(),
        fetchIntelligenceFeatureFlags(),
        fetchIntelligenceFreshness(),
        fetchIntelligenceLineage(undefined, 25),
      ]);
      setDashboard(dashboardResponse);
      setUsage(usageResponse);
      setObservability(observabilityResponse);
      setRevenue(revenueResponse);
      setTiers(tiersResponse);
      setFeatureFlags(flagsResponse);
      setFreshnessRows(freshnessResponse);
      setLineageRows(lineageResponse);
      setStatus("ready");
    } catch {
      setStatus("error");
      setError("Unable to load intelligence dashboard data.");
    }
  };

  useEffect(() => {
    void load(usageDays, invoiceMonth);
  }, []);

  const freshnessVariant = useMemo(() => {
    if (!dashboard) return "secondary";
    if (dashboard.freshness_sec <= 86400) return "success";
    if (dashboard.freshness_sec <= 172800) return "warning";
    return "danger";
  }, [dashboard]);

  const onExportInvoice = async () => {
    setExporting(true);
    try {
      const file = await exportIntelligenceInvoices(invoiceMonth);
      const blobUrl = URL.createObjectURL(file.blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = file.filename || `intelligence-invoices-${invoiceMonth}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } finally {
      setExporting(false);
    }
  };

  const ready =
    status === "ready" &&
    dashboard &&
    usage &&
    observability &&
    revenue;

  return (
    <>
      <PageTitle title="Intelligence" />

      {status === "loading" ? <FullScreenLoader /> : null}

      {status === "error" ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {ready ? (
        <>
          <Row className="g-3 mb-4">
            <Col xs={12} md={3}>
              <Card className="pz-stat">
                <Card.Body>
                  <Eyebrow>Dataset freshness</Eyebrow>
                  <div className="pz-stat__value">{formatFreshness(dashboard.freshness_sec)}</div>
                  <div className="pz-stat__meta">
                    <span className={`badge bg-${freshnessVariant}`}>
                      {freshnessVariant === "success" ? "✓ fresh" : freshnessVariant === "warning" ? "! ageing" : "✕ stale"}
                    </span>
                    <span>since last generation</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} md={3}>
              <MetricCard label="Schema version" value={dashboard.schema_version} />
            </Col>
            <Col xs={12} md={3}>
              <MetricCard label="Total rows" value={formatNumber(dashboard.total_rows)} />
            </Col>
            <Col xs={12} md={3}>
              <Card className="pz-stat">
                <Card.Body>
                  <Eyebrow as="label" className="mb-1">
                    <span id="intelligence-usage-window-label">Usage window</span>
                  </Eyebrow>
                  <Form.Select
                    aria-labelledby="intelligence-usage-window-label"
                    size="sm"
                    value={usageDays}
                    onChange={(e) => {
                      const days = Number(e.target.value);
                      setUsageDays(days);
                      void load(days, invoiceMonth);
                    }}
                  >
                    <option value={7}>7 days</option>
                    <option value={30}>30 days</option>
                    <option value={90}>90 days</option>
                  </Form.Select>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xs={12} md={4}>
              <MetricCard label="Model version" value={dashboard.model_version} />
            </Col>
            <Col xs={12} md={4}>
              <MetricCard label="Training window" value={dashboard.model_training_window} />
            </Col>
            <Col xs={12} md={4}>
              <MetricCard label="Build hash" value={dashboard.model_build_hash} />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xs={12} md={3}>
              <MetricCard label="API uptime" value={formatFreshness(observability.uptime_sec)} />
            </Col>
            <Col xs={12} md={3}>
              <MetricCard label="p95 latency" value={formatLatency(observability.p95_latency_ms)} />
            </Col>
            <Col xs={12} md={3}>
              <MetricCard label="Error rate" value={`${twoDecimalFormatter.format(observability.error_rate_pct)}%`} />
            </Col>
            <Col xs={12} md={3}>
              <MetricCard label="Pipeline success" value={`${twoDecimalFormatter.format(observability.pipeline_success_rate_pct)}%`} />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xs={12} md={4}>
              <MetricCard label="Monthly MRR" value={formatCurrency(revenue.mrr_usd)} />
            </Col>
            <Col xs={12} md={4}>
              <MetricCard label="Endpoint value" value={formatCurrency(revenue.endpoint_usage_value_usd)} />
            </Col>
            <Col xs={12} md={4}>
              <MetricCard label="Top partner share" value={`${twoDecimalFormatter.format(revenue.top_partner_share_pct)}%`} />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xs={12} lg={6}>
              <Card className="h-100">
                <Card.Header>Top categories</Card.Header>
                <Card.Body className="p-0">
                  <Table responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Category</th>
                        <th className="text-end">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.top_categories.length === 0 ? (
                        <tr>
<td colSpan={2} className="p-0">
<EmptyState variant="compact" title="No category trends available" />
</td>
</tr>
                      ) : (
                        dashboard.top_categories.map((row) => (
                          <tr key={row.name}>
                            <td className="fw-semibold">{row.name}</td>
                            <td className="text-end">{formatNumber(row.count)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} lg={6}>
              <Card className="h-100">
                <Card.Header>Rising relationship segments</Card.Header>
                <Card.Body className="p-0">
                  <Table responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Relationship</th>
                        <th className="text-end">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.top_relationship_segments.length === 0 ? (
                        <tr>
<td colSpan={2} className="p-0">
<EmptyState variant="compact" title="No relationship trend data available" />
</td>
</tr>
                      ) : (
                        dashboard.top_relationship_segments.map((row) => (
                          <tr key={row.name}>
                            <td className="fw-semibold">{row.name}</td>
                            <td className="text-end">{formatNumber(row.count)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mb-4">
            <Card.Header>Insight cards</Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Type</th>
                    <th>Segment</th>
                    <th>Direction</th>
                    <th className="text-end">Trend Score</th>
                    <th className="text-end">Confidence</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.insight_cards.length === 0 ? (
                    <tr>
<td colSpan={6} className="p-0">
<EmptyState variant="compact" title="No semantic insights available" />
</td>
</tr>
                  ) : (
                    dashboard.insight_cards.map((card) => (
                      <tr key={card.id}>
                        <td>{card.insight_type}</td>
                        <td>{card.segment}</td>
                        <td>{card.trend_direction}</td>
                        <td className="text-end">{twoDecimalFormatter.format(card.trend_score)}</td>
                        <td className="text-end">
                          {twoDecimalFormatter.format(card.confidence_score)}
                        </td>
                        <td>{card.summary_text}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>Partner usage</Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Partner</th>
                    <th>Endpoint</th>
                    <th className="text-end">Requests</th>
                    <th className="text-end">Rows Returned</th>
                    <th className="text-end">Avg Latency</th>
                  </tr>
                </thead>
                <tbody>
                  {usage.items.length === 0 ? (
                    <tr>
<td colSpan={5} className="p-0">
<EmptyState variant="compact" title="No usage data available" />
</td>
</tr>
                  ) : (
                    usage.items.map((row) => (
                      <tr key={`${row.partner_id}-${row.endpoint}`}>
                        <td className="fw-semibold">{row.partner_name}</td>
                        <td>{row.endpoint}</td>
                        <td className="text-end">{formatNumber(row.request_count)}</td>
                        <td className="text-end">{formatNumber(row.rows_returned_total)}</td>
                        <td className="text-end">{formatLatency(row.avg_latency_ms)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>Tier configuration</Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Tier</th>
                    <th className="text-end">RPM</th>
                    <th className="text-end">Daily Quota</th>
                    <th className="text-end">Burst</th>
                    <th className="text-end">Max Segments</th>
                    <th className="text-end">Max Filters</th>
                    <th>Support</th>
                    <th className="text-end">ACV</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.length === 0 ? (
                    <tr>
<td colSpan={8} className="p-0">
<EmptyState variant="compact" title="No tier configuration rows available" />
</td>
</tr>
                  ) : (
                    tiers.map((tier) => (
                      <tr key={tier.tier_name}>
                        <td className="fw-semibold">{tier.tier_name}</td>
                        <td className="text-end">{formatNumber(tier.requests_per_minute)}</td>
                        <td className="text-end">{formatNumber(tier.daily_quota)}</td>
                        <td className="text-end">{formatNumber(tier.burst_limit)}</td>
                        <td className="text-end">{formatNumber(tier.max_segments)}</td>
                        <td className="text-end">{formatNumber(tier.max_filters)}</td>
                        <td>{tier.support_level}</td>
                        <td className="text-end">{formatCurrency(tier.price_acv)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>Feature flags</Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Flag</th>
                    <th>Enabled</th>
                    <th className="text-end">Rollout %</th>
                    <th>Partner Scope</th>
                  </tr>
                </thead>
                <tbody>
                  {featureFlags.length === 0 ? (
                    <tr>
<td colSpan={4} className="p-0">
<EmptyState variant="compact" title="No feature flags configured" />
</td>
</tr>
                  ) : (
                    featureFlags.map((flag) => (
                      <tr key={flag.flag_name}>
                        <td className="fw-semibold">{flag.flag_name}</td>
                        <td>{flag.enabled ? "true" : "false"}</td>
                        <td className="text-end">{twoDecimalFormatter.format(flag.rollout_percentage)}</td>
                        <td>{flag.partner_scope.length ? flag.partner_scope.join(", ") : "--"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Row className="g-3 mb-4">
            <Col xs={12} lg={6}>
              <Card className="h-100">
                <Card.Header>Dataset freshness by table</Card.Header>
                <Card.Body className="p-0">
                  <Table responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Dataset</th>
                        <th className="text-end">Freshness</th>
                        <th>Pipeline</th>
                        <th>Contract</th>
                      </tr>
                    </thead>
                    <tbody>
                      {freshnessRows.length === 0 ? (
                        <tr>
<td colSpan={4} className="p-0">
<EmptyState variant="compact" title="No freshness rows available" />
</td>
</tr>
                      ) : (
                        freshnessRows.map((row) => (
                          <tr key={row.dataset_name}>
                            <td className="fw-semibold">{row.dataset_name}</td>
                            <td className="text-end">{formatFreshness(row.freshness_sec)}</td>
                            <td>{row.pipeline_version}</td>
                            <td>{row.contract_version}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} lg={6}>
              <Card className="h-100">
                <Card.Header>Lineage audit trail</Card.Header>
                <Card.Body className="p-0">
                  <Table responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Dataset</th>
                        <th>Pipeline</th>
                        <th className="text-end">Source Events</th>
                        <th>Model version</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineageRows.length === 0 ? (
                        <tr>
<td colSpan={4} className="p-0">
<EmptyState variant="compact" title="No lineage rows available" />
</td>
</tr>
                      ) : (
                        lineageRows.map((row, index) => (
                          <tr key={`${row.dataset_name}-${row.generated_at}-${index}`}>
                            <td className="fw-semibold">{row.dataset_name}</td>
                            <td>{row.source_pipeline}</td>
                            <td className="text-end">{formatNumber(row.source_event_count)}</td>
                            <td>{row.model_version || "--"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mb-4">
            <Card.Header>Segment trends</Card.Header>
            <Card.Body className="p-0">
              <Table responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Age Group</th>
                    <th>Gender</th>
                    <th>Relationship</th>
                    <th>Occasion</th>
                    <th>Event</th>
                    <th className="text-end">Count</th>
                    <th className="text-end">Sample Size</th>
                    <th className="text-end">Confidence</th>
                    <th>Segment Definition</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.segment_trends.length === 0 ? (
                    <tr>
<td colSpan={9} className="p-0">
<EmptyState variant="compact" title="No segment trend rows available" />
</td>
</tr>
                  ) : (
                    dashboard.segment_trends.map((row, index) => (
                      <tr
                        key={`${row.segment_occasion}-${row.segment_relationship}-${row.event_type}-${index}`}
                      >
                        <td>{row.segment_age_group || "--"}</td>
                        <td>{row.segment_gender || "--"}</td>
                        <td>{row.segment_relationship || "--"}</td>
                        <td>{row.segment_occasion || "--"}</td>
                        <td>{row.event_type || "--"}</td>
                        <td className="text-end">{formatNumber(Number(row.count || 0))}</td>
                        <td className="text-end">{formatNumber(Number(row.sample_size || 0))}</td>
                        <td className="text-end">
                          {row.confidence_score === undefined || row.confidence_score === null
                            ? "--"
                            : twoDecimalFormatter.format(row.confidence_score)}
                        </td>
                        <td>{row.segment_definition?.label || "--"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>Invoice export</Card.Header>
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col xs={12} md={4}>
                  <Form.Label>Invoice month</Form.Label>
                  <Form.Control
                    type="month"
                    value={invoiceMonth}
                    onChange={(e) => {
                      const month = e.target.value;
                      setInvoiceMonth(month);
                      void load(usageDays, month);
                    }}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Button onClick={onExportInvoice} disabled={exporting || !invoiceMonth}>
                    {exporting ? "Exporting…" : "Export invoice CSV"}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </>
      ) : null}
    </>
  );
};

export default IntelligencePage;
